import { useState, useCallback } from "react";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const useResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const handleRequestResetCode = useCallback(async (emailAddress: string) => {
    try {
      setLoading(true);
      const output = await resetPassword({ username: emailAddress });

      if (output) {
        setEmail(emailAddress);
        setStep("confirm");
        toast.success("Reset code sent!", {
          description: "Please check your email for the verification code.",
        });
        return true;
      }
    } catch (error: any) {
      console.error("handleRequestResetCode:: ", error);

      if (error?.name === "UserNotFoundException") {
        toast.error("User not found", {
          description: "No account found with this email address.",
        });
      } else if (error?.name === "LimitExceededException") {
        toast.error("Too many requests", {
          description: "Please wait before requesting another code.",
        });
      } else {
        toast.error("Error sending reset code", {
          description: error?.message || "Please try again later.",
        });
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmResetPassword = useCallback(
    async (code: string, newPassword: string) => {
      try {
        setLoading(true);
        await confirmResetPassword({
          username: email,
          confirmationCode: code,
          newPassword: newPassword,
        });

        toast.success("Password reset successful!", {
          description: "You can now sign in with your new password.",
        });
        setEmail("");
        navigate("/login");
        return true;
      } catch (error: any) {
        console.error("handleConfirmResetPassword:: ", error);

        if (error?.name === "CodeMismatchException") {
          toast.error("Invalid code", {
            description: "The verification code is incorrect or expired.",
          });
        } else if (error?.name === "ExpiredCodeException") {
          toast.error("Code expired", {
            description: "The verification code has expired. Please request a new one.",
          });
        } else if (error?.name === "InvalidPasswordException") {
          toast.error("Invalid password", {
            description: "Password does not meet requirements.",
          });
        } else {
          toast.error("Error resetting password", {
            description: error?.message || "Please try again.",
          });
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [email]
  );

  const goBackToRequest = useCallback(() => {
    setStep("request");
    setEmail("");
  }, []);

  return {
    loading,
    step,
    email,
    handleRequestResetCode,
    handleConfirmResetPassword,
    goBackToRequest,
  };
};

export default useResetPassword;
